import { RegisterRequest } from '@/types/register';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pencil, Check, X } from 'lucide-react';
import { MARITAL_STATUS_AR, EDUCATION_LEVEL_AR } from '@/utils/translations';

interface RegisterRequestListProps {
  requests: RegisterRequest[];
  onEdit: (request: RegisterRequest) => void;
  onApprove: (request: RegisterRequest) => void;
  onReject: (request: RegisterRequest) => void;
}

export function RegisterRequestList({ 
  requests, 
  onEdit,
  onApprove,
  onReject
}: RegisterRequestListProps) {
  return (
    <div className="space-y-4">
      {requests.map((request) => (
        <div key={request.id} className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex justify-between items-start">
            <div className="space-y-4 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">{request.name}</h3>
                <Badge variant="secondary">{request.occupation}</Badge>
                <Badge variant="warning">قيد المراجعة</Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                {/* Personal Information */}
                <div className="space-y-2">
                  <h4 className="font-medium text-primary">المعلومات الشخصية</h4>
                  <p>
                    <span className="font-medium ml-1">رقم الجوال:</span>
                    <span dir="ltr">{request.phoneNumber}</span>
                  </p>
                  <p>
                    <span className="font-medium ml-1">تاريخ الميلاد:</span>
                    {`${request.birthDate.year}/${request.birthDate.month}/${request.birthDate.day}`}
                    <span className="text-xs text-gray-500 mr-1">
                      ({request.birthDate.type === 'hijri' ? 'هجري' : 'ميلادي'})
                    </span>
                  </p>
                  <p>
                    <span className="font-medium ml-1">الحالة الاجتماعية:</span>
                    {MARITAL_STATUS_AR[request.maritalStatus]}
                  </p>
                </div>

                {/* Family Information */}
                <div className="space-y-2">
                  <h4 className="font-medium text-primary">معلومات العائلة</h4>
                  <p>
                    <span className="font-medium ml-1">اسم الأب:</span>
                    {request.fatherName}
                  </p>
                  <p>
                    <span className="font-medium ml-1">اسم الجد:</span>
                    {request.grandfather1}
                  </p>
                  <p>
                    <span className="font-medium ml-1">اسم الجد الثاني:</span>
                    {request.grandfather2}
                  </p>
                  <p>
                    <span className="font-medium ml-1">اسم الجد الثالث:</span>
                    {request.grandfather3}
                  </p>
                </div>

                {/* Additional Information */}
                <div className="space-y-2">
                  <h4 className="font-medium text-primary">معلومات إضافية</h4>
                  <p>
                    <span className="font-medium ml-1">المنطقة:</span>
                    {request.region}
                  </p>
                  <p>
                    <span className="font-medium ml-1">المهنة:</span>
                    {request.occupation}
                  </p>
                  <p>
                    <span className="font-medium ml-1">الفرع:</span>
                    {request.branch}
                  </p>
                  <p>
                    <span className="font-medium ml-1">المرحلة الدراسية:</span>
                    {request.educationLevel === 'other' 
                      ? request.otherEducationLevel 
                      : EDUCATION_LEVEL_AR[request.educationLevel]}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onEdit(request)}
                  className="hover:bg-secondary"
                  title="تعديل"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onApprove(request)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  title="قبول"
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onReject(request)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  title="رفض"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}